@file:UseSerializers(UuidSerializer::class)

package it.neckar.lizergy.model.configuration.moduleLayout

import com.meistercharts.charts.pv.roofPlanning.ModuleSize
import it.neckar.lizergy.lifeCycle.LizergyHasLifeCycle
import it.neckar.lizergy.lifeCycle.LizergyLifeCycleState
import it.neckar.lizergy.model.configuration.quote.builder.Manufacturer
import it.neckar.lizergy.model.energy.Current
import it.neckar.lizergy.model.energy.PowerRating
import it.neckar.lizergy.model.energy.Voltage
import it.neckar.open.annotations.GermanTerm
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.uuid.HasUuid
import it.neckar.uuid.UuidSerializer
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import kotlin.uuid.Uuid

/**
 * Represents a PV module type
 */
@Serializable
data class PvModule(
  val id: PvModuleId,

  /**
   * The name / label of the module
   */
  val description: String,

  /**
   * The power rating for this module
   */
  @GermanTerm("Nennleistung")
  val powerRating: PowerRating,
  /**
   * The open circuit voltage
   */
  @GermanTerm("Leerlaufspannung")
  val offLoadVoltage: Voltage = Voltage(0.0),
  /**
   * The short circuit current
   */
  @GermanTerm("Kurzschlussstrom")
  val shortCircuitCurrent: Current = Current(0.0),

  /**
   * The size of the module
   */
  val size: ModuleSize,

  val material: PvModuleTypeMaterial = PvModuleTypeMaterial.Normal,

  val warranty: Int = 30,

  val dataSheetPath: String? = null,

  val compatibleWithManufacturer: Manufacturer = Manufacturer.SMA,

  override val lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,

  ) : HasUuid, LizergyHasLifeCycle {

  override val uuid: Uuid
    get() = id.uuid

  @GermanTerm("Schwarzes Modul")
  val isBlackModule: Boolean
    get() = when (material) {
      PvModuleTypeMaterial.GlassGlass -> true
      PvModuleTypeMaterial.Normal -> false
    }

  fun format(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return buildString {
      append(description)
      append(" / ")
      append(powerRating.formatWattPeak(i18nConfiguration, whitespaceConfig))
      append(" / ")
      append(size.format(i18nConfiguration, whitespaceConfig))
      append(" / ")
      append("Uoc")
      append(whitespaceConfig.smallSpace)
      append(offLoadVoltage.format(i18nConfiguration, whitespaceConfig))
      append(" / ")
      append("Isc")
      append(whitespaceConfig.smallSpace)
      append(shortCircuitCurrent.format(i18nConfiguration, whitespaceConfig))

    }
  }

  /**
   * An id for a PvModuleType
   */
  @Serializable
  data class PvModuleId(val uuid: Uuid) {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): PvModuleId {
        return PvModuleId(randomUuid4())
      }
    }
  }
}

enum class PvModuleTypeMaterial {
  Normal,
  GlassGlass,
}
